<script setup>
import { VueperSlides, VueperSlide } from 'vueperslides'
import { useScreen } from '@/composables/useScreen'
import { computed } from 'vue'

const { reviews } = defineProps({
  reviews: {
    type: Array,
    default: () => [],
    required: true
  }
})

const { isMobileView } = useScreen()
const visibleSlidesCount = computed(() => {
  if (isMobileView.value || reviews.length <= 1) {
    return 1
  }

  return 2
})

const arrowShift = computed(() => 45 + (Math.ceil(reviews.length / visibleSlidesCount.value) * (isMobileView.value? 34 : 44) / 2))
const isAutoplayActive = computed(() => reviews.length / visibleSlidesCount.value > 1)
</script>
<template>
  <div>
    <vueper-slides
      class="no-shadow overflow-hidden ml-2 mr-2"
      :visible-slides="visibleSlidesCount"
      slide-multiple
      :gap="0"
      :fixed-height="isMobileView? '500px' : '425px'"
      :arrows="true"
      :autoplay="isAutoplayActive"
      :touchable="false"
    >
      <template #bullet="{ active }">
        <div class="relative mb-[72px] lg:mb-[54px] w-[25px] lg:w-[35px] h-[4px] -mx-[6px] top-12 lg:top-7 bg-white" :class="{'opacity-55' : !active}" />
      </template>
      <template #arrow-left>
        <div class="relative top-[200px] lg:top-[160px]" :style="{left: `-${arrowShift}px`}">
          <IconArrowFullLeft class="text-white" />
        </div>
      </template>
      <template #arrow-right>
        <div class="relative top-[200px] lg:top-[160px]" :style="{right: `-${arrowShift}px`}">
          <IconArrowFullRight class="text-white" />
        </div>
      </template>
      <vueper-slide
        v-for="(review, index) in reviews"
        :key="index"
      >
        <template #content>
          <div class="flex bg-white text-darkBlue lg:h-[312px] rounded-[40px] ml-2 mr-2">
            <div v-if="isMobileView" class="mt-6 ml-6">
              <div class="flex">
                <el-avatar :src="review.user.photo" :size="50">
                  <IconBlankUser alt="reviewer" class="scale-75" />
                </el-avatar>
                <rating-stars :value="review.rating" :text-size="20" class="ml-4" filled-star-class="text-lightBlue mr-1.5" />
              </div>
              <div class="text-darkBlue">
                <div class="text-md leading-normal mt-8 mb-2 mr-6 overflow-y-auto">
                  {{ review.comment }}
                </div>
                <div class="text-18 mt-4 mb-6 font-redhat font-semibold">
                  {{ review.user.name }} {{ review.user.lastname }}
                </div>
              </div>
            </div>
            <div v-else class="mt-6 ml-6 flex">
              <div>
                <el-avatar :src="review.user.photo" :size="70">
                  <IconBlankUser alt="reviewer" class="scale-75" />
                </el-avatar>
              </div>
              <div class="ml-8 text-darkBlue flex flex-col">
                <rating-stars :value="review.rating" :text-size="24" filled-star-class="text-lightBlue mr-1.5" />
                <div class="text-md leading-normal mt-8 mb-2 mr-6 overflow-y-auto">
                  {{ review.comment }}
                </div>
                <div class="text-lg mt-auto mb-6 font-redhat font-semibold">
                  {{ review.user.name }} {{ review.user.lastname }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </vueper-slide>
    </vueper-slides>
  </div>
</template>
<style scoped>
:deep(.vueperslides__bullet)s{
  position: absolute;
}

:deep(.vueperslides__arrow) {
  opacity: 1;
}

:deep(.vueperslides__arrow--prev) {
  left: 50%;
}

:deep(.vueperslides__arrow--next) {
  right: 50%;
}
</style>